import { ButtonOverrides } from 'baseui/button'
import { PLACEMENT, Popover } from 'baseui/popover'
import { DateTime } from 'luxon'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useStyletron } from '../../../../theme'
import capitalize from '../../../../utils/capitalize'
import { BUTTON_KIND } from '../../../Buttons'
import Button from '../../../Buttons/Button'
import { MonthPickerMenu, MonthPickerMenuProps } from './MonthPickerMenu'

/**
 * @interface MonthPickerProps
 * @extends MonthPickerMenuProps {@link ./MonthPickerMenu}
 * @member {string} placement of the popover when clicking the month picker button
 */
export interface MonthPickerProps extends Omit<MonthPickerMenuProps, 'defaultValue'> {
    placement?: (typeof PLACEMENT)[keyof typeof PLACEMENT]
    buttonOverrides?: ButtonOverrides
    defaultValue?: DateTime
    popupWrapperDivStyles?: string
}

export const MonthPicker = ({
    value = DateTime.now().minus({ month: 1 }),
    placement = PLACEMENT.bottom,
    buttonOverrides,
    popupWrapperDivStyles,
    ...props
}: MonthPickerProps) => {
    const [open, setOpen] = React.useState(false)
    const { i18n } = useTranslation()
    const [css, theme] = useStyletron()

    return (
        <Popover
            isOpen={open}
            triggerType="click"
            focusLock
            onClickOutside={() => setOpen(false)}
            overrides={{
                Body: {
                    style: { backgroundColor: 'transparent', boxShadow: 'none' }
                },
                Inner: { style: { backgroundColor: 'transparent' } }
            }}
            onClick={() => setOpen(!open)}
            placement={placement}
            content={
                <MonthPickerMenu
                    {...props}
                    value={value}
                    onChange={(value) => {
                        props?.onChange && props?.onChange(value)
                        setOpen(false)
                    }}
                />
            }
        >
            <div className={popupWrapperDivStyles}>
                <Button
                    $kind={BUTTON_KIND.tertiary}
                    endEnhancer={
                        !open ? (
                            <i
                                className={`wemolo-chevron-down ${css({
                                    fontSize: theme.sizing.scale600
                                })}`}
                            />
                        ) : (
                            <i
                                className={`wemolo-chevron-up ${css({
                                    fontSize: theme.sizing.scale600
                                })}`}
                            />
                        )
                    }
                    overrides={{
                        ...(buttonOverrides || {}),
                        BaseButton: {
                            style: {
                                backgroundColor: 'transparent',
                                background: 'transparent',
                                color: theme.colors.contentAccent,
                                fontWeight: 300,
                                border: `1px solid ${theme.colors.mono200}`,
                                height: '50px',
                                borderColor: theme.colors.mono200,
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                ':hover': {
                                    border: `1px solid ${theme.colors.mono200}`,
                                    color: theme.colors.contentAccent,
                                    backgroundColor: 'transparent'
                                },
                                ':active': {
                                    border: `1px solid ${theme.colors.mono200}`,
                                    color: theme.colors.contentAccent,
                                    backgroundColor: 'transparent'
                                },
                                ':focus': {
                                    border: `1px solid ${theme.colors.mono200}`,
                                    color: theme.colors.contentAccent,
                                    backgroundColor: 'transparent'
                                },
                                ...(buttonOverrides?.BaseButton?.style || {})
                            }
                        },
                        EndEnhancer: {
                            style: {
                                color: theme.colors.contentAccent,
                                fontSize: theme.sizing.scale700
                            }
                        }
                    }}
                >
                    {capitalize(value.setLocale(i18n.language).toFormat('MMMM, yyyy'))}
                </Button>
            </div>
        </Popover>
    )
}
